import { useEffect, useRef } from "react";

const TRAIL_LENGTH = 8;
const UGL_LOGO = "/ugl-logo.png";

export default function CursorTrail() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -100, y: -100 });
  const trailRef = useRef([]);
  const imgRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // Check reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (document.documentElement.classList.contains('a11y-no-animations')) return;
    // Skip on mobile
    if ('ontouchstart' in window) return;

    const img = new Image();
    img.src = UGL_LOGO;
    img.onload = () => { imgRef.current = img; };

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMove);

    // Init trail
    for (let i = 0; i < TRAIL_LENGTH; i++) {
      trailRef.current.push({ x: -100, y: -100 });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const trail = trailRef.current;

      // Smooth follow
      trail[0].x += (mouseRef.current.x - trail[0].x) * 0.3;
      trail[0].y += (mouseRef.current.y - trail[0].y) * 0.3;
      for (let i = 1; i < trail.length; i++) {
        trail[i].x += (trail[i - 1].x - trail[i].x) * 0.25;
        trail[i].y += (trail[i - 1].y - trail[i].y) * 0.25;
      }

      // Draw trail particles (glow)
      for (let i = trail.length - 1; i >= 1; i--) {
        const t = 1 - i / trail.length;
        const alpha = t * 0.3;
        const size = 4 + t * 6;

        // Gold glow particle
        ctx.beginPath();
        ctx.arc(trail[i].x, trail[i].y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201, 168, 76, ${alpha})`;
        ctx.fill();

        // Outer glow
        ctx.beginPath();
        ctx.arc(trail[i].x, trail[i].y, size * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 160, 209, ${alpha * 0.3})`;
        ctx.fill();
      }

      // Draw UGL logo at cursor position
      if (imgRef.current) {
        const logoSize = 24;
        const x = trail[0].x + 12;
        const y = trail[0].y + 12;

        // Logo glow
        ctx.shadowColor = 'rgba(201, 168, 76, 0.4)';
        ctx.shadowBlur = 12;
        ctx.globalAlpha = 0.8;
        ctx.drawImage(imgRef.current, x - logoSize / 2, y - logoSize / 2, logoSize, logoSize);
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[9998] pointer-events-none"
      aria-hidden="true"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}
